import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';

import FormInput from '../../../components/form/FormInput';
import {
  broadcastAdd,
  broadcastUpdate,
  broadcastDetailsGetById,
  whatsappListGet,
  whatsappTemplatesGet
} from '../../../store/slices/tenantSlices/configurationSlice';
import FormSelect from '../../../components/form/FormSelect';
import { crmsOptionGet } from '../../../store/slices/tenantSlices/dailyReportSlice';
import FormDateInput from '../../../components/form/FormDateInput';
import { endOfDay, startOfDay, parse } from 'date-fns';
import FormToggle from '../../../components/form/FormToggle';
import { delayTimeOptions, formatPayloadDate } from '../../../utils/common';
import { toZonedTime } from 'date-fns-tz';
import { useTimezone } from '../../../store/slices/commonSlice';

const AddEditWhatsappBroadcastModal = ({
  isOpen,
  onCloseClick,
  isEdit = false,
  editRecord = null
}) => {
  const dispatch = useDispatch();
  const timezone = useTimezone();
  const today = toZonedTime(new Date(), timezone);
  const [crmOptions, setCrmOptions] = useState([]);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [whatsappOptions, setWhatsappOptions] = useState([]);

  const initialValues = {
    name: '',
    crm_id: '',
    whatsapp_template_id: '',
    whatsapp_id: '',
    start_date: startOfDay(today),
    end_date: endOfDay(today),
    status: 'on',
    country_code: '',
    delay_time: '',
    time_condition: 'no'
  };

  const validationSchema = Yup.object({
    name: Yup.string().required('name is required'),
    crm_id: Yup.string().required('crm is required'),
    whatsapp_template_id: Yup.string().required('whatsapp template is required'),
    whatsapp_id: Yup.string().required('whatsapp number is required'),
    delay_time: Yup.string().required('delay time is required'),
    country_code: Yup.string().required('country code is required')
  });

  const onSubmit = async () => {
    if (isEdit) {
      try {
        const formattedStartDate = formatPayloadDate(values.start_date);
        const formattedEndDate = formatPayloadDate(values.end_date);
        const payload = { ...values, id: editRecord.whatsapp_broadcast_id };
        payload['start_date'] = formattedStartDate;
        payload['end_date'] = formattedEndDate;
        if (values.time_condition === 'no') {
          delete payload['start_date'];
          delete payload['end_date'];
        }
        const response = await dispatch(broadcastUpdate(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Updated Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    } else {
      try {
        const formattedStartDate = formatPayloadDate(values.start_date);
        const formattedEndDate = formatPayloadDate(values.end_date);
        const payload = { ...values };
        payload['start_date'] = formattedStartDate;
        payload['end_date'] = formattedEndDate;
        if (values.time_condition === 'no') {
          delete payload['start_date'];
          delete payload['end_date'];
        }
        const response = await dispatch(broadcastAdd(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Added Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setValues,
    setFieldValue
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  const getCrmsOption = async () => {
    try {
      const response = await dispatch(crmsOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setCrmOptions(options);
      } else {
        setCrmOptions([]);
      }
    } catch (error) {
      setCrmOptions([]);
      console.log('error', error);
    }
  };

  const getTemplatesOption = async () => {
    try {
      const response = await dispatch(whatsappTemplatesGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.template_name,
          value: val.id
        }));
        setTemplateOptions(options);
      } else {
        setTemplateOptions([]);
      }
    } catch (error) {
      setTemplateOptions([]);
      console.log('error', error);
    }
  };

  const getWhatsappOption = async () => {
    try {
      const response = await dispatch(whatsappListGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.whatsapp_number,
          value: val.id
        }));
        setWhatsappOptions(options);
      } else {
        setWhatsappOptions([]);
      }
    } catch (error) {
      setWhatsappOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      getWhatsappOption();
      getCrmsOption();
      getTemplatesOption();
    }
  }, [isOpen]);

  const fetchListRecord = async () => {
    try {
      const response = await dispatch(
        broadcastDetailsGetById(editRecord?.whatsapp_broadcast_id)
      ).unwrap();
      if (response && response?.data) {
        const startTime = response?.data.from_date
          ? parse(response?.data.from_date, 'yyyy-MM-dd HH:mm:ss', today)
          : startOfDay(today);
        const endTime = response?.data.to_date
          ? parse(response?.data.to_date, 'yyyy-MM-dd HH:mm:ss', today)
          : endOfDay(today);
        setValues({
          name: response?.data?.name,
          crm_id: response?.data?.crm_id,
          whatsapp_template_id: response?.data?.whatsapp_template_id,
          whatsapp_id: response?.data?.whatsapp_id,
          country_code: response?.data?.country_code,
          delay_time: response?.data?.delay_time,
          status: response?.data?.status || 'on',
          time_condition: response?.data?.time_condition || 'no',
          start_date: startTime,
          end_date: endTime
        });
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (isEdit) {
      fetchListRecord();
    }
  }, [editRecord]);

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit Whatsapp Broadcast' : 'Add Whatsapp Broadcast'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="whatsappBroadcast" onSubmit={handleSubmit}>
          <Row>
            <Col lg="3">
              <FormToggle
                trueName="Yes"
                falseName="No"
                name="time_condition"
                label="Time Based"
                value={String(values.time_condition === 'yes')}
                onChange={() => {
                  setFieldValue('time_condition', values.time_condition === 'yes' ? 'no' : 'yes');
                  if (values.time_condition === 'no') {
                    setFieldValue('start_date', startOfDay(today));
                    setFieldValue('end_date', endOfDay(today));
                  }
                }}
              />
            </Col>
            {values?.time_condition === 'yes' && (
              <Col lg="3">
                <FormDateInput
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  label="Start Date"
                  showTimeSelect={true}
                  name="start_date"
                  value={values.start_date}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            )}
            {values?.time_condition === 'yes' && (
              <Col lg="3">
                <FormDateInput
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  label="End Date"
                  showTimeSelect={true}
                  name="end_date"
                  value={values.end_date}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            )}
            <Col lg="3">
              <FormInput
                name="name"
                label="Name"
                placeholder="Enter Name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                trueName="On"
                falseName="Off"
                name="status"
                label="Status"
                value={String(values.status === 'on')}
                onChange={() => setFieldValue('status', values.status === 'on' ? 'off' : 'on')}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                label="CRM"
                placeholder="Select CRM"
                value={values.crm_id}
                onChange={handleChange}
                onBlur={handleBlur}
                name="crm_id"
                options={crmOptions}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                label="Whatsapp Templates"
                placeholder="Select Templates"
                value={values.whatsapp_template_id}
                onChange={handleChange}
                onBlur={handleBlur}
                name="whatsapp_template_id"
                options={templateOptions}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                label="Whatsapp number"
                placeholder="Select number"
                value={values.whatsapp_id}
                onChange={handleChange}
                onBlur={handleBlur}
                name="whatsapp_id"
                options={whatsappOptions}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                label="Delay Time"
                placeholder="Select time"
                value={values.delay_time}
                onChange={handleChange}
                onBlur={handleBlur}
                name="delay_time"
                options={delayTimeOptions}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormInput
                name="country_code"
                label="Country code"
                placeholder="Enter Country code"
                value={values.country_code}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
          </Row>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button form="whatsappBroadcast" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditWhatsappBroadcastModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any
};

export default AddEditWhatsappBroadcastModal;
